import { Injectable } from '@angular/core';
import { take } from 'rxjs';
import { LIVAnalyticsModel } from 'src/app/core/models/liv-analytics.model';
import { Tracking } from 'src/app/features/cycles/shared/types';
import { AuthProps, AuthStore } from 'src/app/shared/store/auth.store';

import { KibanaService } from './api/requests/kibana.service';

@Injectable({
  providedIn: 'root'
})
export class LIVAnalyticsService {
  constructor(
    private kibana: KibanaService,
    private authStore: AuthStore
  ) {}

  // Função para formatar os dados do usuário
  private formatUserData(data: AuthProps): LIVAnalyticsModel['userData'] {
    if (!data) {
      return null;
    }

    return {
      id: data.id,
      nome: data.nome,
      email: data.email,
      perfisAcesso: data.perfisAcesso,
      espacoSer: data.espacoSer
    };
  }

  // Função para criar o payload base
  private createBasePayload(
    auth: AuthProps,
    target?: HTMLElement
  ): Omit<LIVAnalyticsModel, 'trackingId' | 'objectData'> {
    const url = window.location.href;

    return {
      url,
      urlTo: target?.baseURI || url, // Usa a URL atual se target não for fornecido
      type: 'click',
      cssClasses: target?.classList?.value || '', // Usa string vazia se target não for fornecido
      htmlId: target?.id || '', // Usa string vazia se target não for fornecido
      htmlTag: target?.tagName || '', // Usa string vazia se target não for fornecido
      htmlText: target?.textContent?.trim() || null, // Usa null se target não for fornecido
      linkHref: target?.getAttribute('href') || null, // Usa null se target não for fornecido
      imgSrc: target?.getAttribute('src') || null, // Usa null se target não for fornecido
      imgAlt: target?.getAttribute('alt') || null, // Usa null se target não for fornecido
      userData: this.formatUserData(auth)
    };
  }

  public trackEventFromTarget(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.hasAttribute('data-tracking-id')) {
      return;
    }

    const userData = { ...this.authStore.authSnapshot };

    if (!userData?.token) {
      return;
    }

    const trackingId = target.getAttribute('data-tracking-id');
    const trackingObjectData = target.getAttribute('data-tracking-object');

    const basePayload = this.createBasePayload(userData, target);

    const trackingPayload: LIVAnalyticsModel = {
      ...basePayload,
      trackingId,
      objectData: trackingObjectData
    };

    this.saveEvent(trackingPayload);
  }

  public trackEvent(tracking: Tracking, event?: Event): void {
    const userData = { ...this.authStore.authSnapshot };

    if (!userData?.token) {
      return;
    }

    const target = event?.target as HTMLElement;

    const basePayload = this.createBasePayload(userData, target);

    const trackingPayload: LIVAnalyticsModel = {
      ...basePayload,
      trackingId: tracking.id,
      objectData: tracking.data
        ? JSON.stringify(tracking.data)
        : target?.getAttribute('data-tracking-object') || null
    };

    this.saveEvent(trackingPayload);
  }

  private saveEvent(trackingPayload: LIVAnalyticsModel) {
    return this.kibana
      .saveAnalyticsEvent(trackingPayload)
      .pipe(take(1))
      .subscribe(() => {
        console.info('Event saved successfully 🚀');
      });
  }
}
